import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { formatMoney, minorUnits } from '@/lib/money'
import { NoAccess, StaffGate, isForbidden, useApiErrorText } from '@/lib/staffGate'
import type { StaffSession } from '@/lib/staffSession'

import {
  useCheckInParticipant,
  useCheckinSummary,
  useCollectAtDoor,
  useDistributeBracelet,
  useDoorTill,
  useScan,
  useUndoCheckin,
} from '../api'
import { TablePicker } from '../TablePicker'
import { DoorCounter } from '../DoorCounter'
import { QrScanner } from '../QrScanner'
import type {
  DoorCollectMethod,
  DoorCollectResult,
  ScanParticipant,
  ScanResponse,
  ScanResult,
} from '../types'

/** Couleur unique par issue : lisible d'un mètre, en basse lumière. */
const TONES: Record<ScanResult, { border: string; text: string; label: string }> = {
  valid: { border: 'border-state-success', text: 'text-state-success', label: 'checkin.valid' },
  already_checked_in: {
    border: 'border-state-danger',
    text: 'text-state-danger',
    label: 'checkin.already',
  },
  invalid_token: {
    border: 'border-state-danger',
    text: 'text-state-danger',
    label: 'checkin.invalid',
  },
  wrong_event: {
    border: 'border-state-danger',
    text: 'text-state-danger',
    label: 'checkin.wrongEvent',
  },
  unpaid: { border: 'border-ember-400', text: 'text-ember-300', label: 'checkin.unpaid' },
  undone: { border: 'border-line-strong', text: 'text-content-secondary', label: 'checkin.undone' },
}

/** Le motif d'annulation exigé par le serveur : cinq caractères au moins. */
const UNDO_REASON_MIN = 5

/**
 * Poste d'entrée.
 *
 * Un QR, une commande, un groupe. Le scan répond à deux questions d'un coup —
 * **est-ce payé ?** et **qui est attendu ?** — puis le contrôle valide les gens
 * un par un, en les regardant. Personne n'entre parce que quelqu'un d'autre du
 * même billet est arrivé : c'est ce qui permet, à minuit, de savoir combien de
 * personnes sont réellement dans la salle.
 *
 * Une commande pas encore réglée s'encaisse ici même, dans la caisse de
 * l'entrée : renvoyer le groupe « vers la caisse » ne disait pas laquelle.
 *
 * Le détail (nourriture, table, bracelet) reste affiché : il sert à orienter le
 * groupe dans la seconde qui suit son entrée.
 */
export function CheckinPage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('checkin.overline')}>
      {(session, signOut) => <CheckinDesk session={session} onSignOut={signOut} />}
    </StaffGate>
  )
}

function CheckinDesk({ session, onSignOut }: { session: StaffSession; onSignOut: () => void }) {
  const { t, i18n } = useTranslation()
  const errorText = useApiErrorText()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id

  const scan = useScan(eventId)
  const undo = useUndoCheckin(eventId)
  const bracelet = useDistributeBracelet(eventId)
  const checkIn = useCheckInParticipant(eventId)
  const summary = useCheckinSummary(eventId, true)

  const [outcome, setOutcome] = useState<ScanResponse | null>(null)
  const [manualToken, setManualToken] = useState('')
  // Ce qui vient d'être encaissé à la porte : la monnaie, en grand.
  const [collected, setCollected] = useState<DoorCollectResult | null>(null)
  // Un geste refusé avant même de partir (un motif trop court) : on le dit.
  const [notice, setNotice] = useState<string | null>(null)
  // La caméra ne tourne que sur demande. Laissée active toute la soirée, elle
  // vide la batterie du téléphone en une heure — et une caméra allumée sans
  // raison devant une file d'invités se remarque.
  const [scanning, setScanning] = useState(false)

  // La caméra reste allumée mais on ignore les décodages tant qu'un verdict
  // est affiché : sinon la personne suivante « écrase » le résultat en cours.
  const paused = scan.isPending || outcome !== null

  if (isForbidden(summary.error)) {
    return <NoAccess title={t('checkin.overline')} onSignOut={onSignOut} />
  }

  const submit = (token: string) => {
    const trimmed = token.trim()
    if (!trimmed) return
    setCollected(null)
    setNotice(null)
    scan.mutate(
      { token: trimmed, device_label: navigator.userAgent.slice(0, 60) },
      { onSuccess: setOutcome },
    )
  }

  /**
   * Le motif d'une annulation. La fenêtre se fermait sans rien faire quand il
   * était trop court — on croyait avoir annulé l'entrée. Désormais l'écran
   * dit pourquoi rien ne s'est passé.
   */
  const askUndoReason = (): string | null => {
    const reason = window.prompt(t('checkin.undoReason'))
    if (reason === null) return null
    if (reason.trim().length < UNDO_REASON_MIN) {
      setNotice(t('checkin.undoTooShort', { count: UNDO_REASON_MIN }))
      return null
    }
    setNotice(null)
    return reason.trim()
  }

  const tone = outcome ? TONES[outcome.result] : null
  // Le scan d'une commande ne fait entrer personne : annoncer « Entrée
  // validée » ferait croire au contrôle que le groupe est passé, alors qu'il
  // reste à cocher les noms un par un.
  const verdict =
    outcome && outcome.participants.length > 0 && outcome.result === 'valid'
      ? 'checkin.orderValid'
      : outcome && outcome.participants.length > 0 && outcome.result === 'already_checked_in'
        ? 'checkin.orderAlready'
        : // Une table sans entrée comprise : ce QR retrouve la table, il ne
          // fait entrer personne. « Entrée validée » ferait passer le groupe
          // sans ses billets.
          outcome &&
            outcome.result === 'valid' &&
            outcome.participants.length === 0 &&
            outcome.table_admits === 0
          ? 'checkin.tableOnly'
          : tone?.label
  const actionError = errorText(undo.error ?? checkIn.error ?? bracelet.error)

  return (
    <Container className="py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          {/* La langue à côté du nom de l'écran : le coin « compte » garde
              sa place, et rien ne pousse « Se déconnecter » hors de l'écran. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Overline>{t('checkin.overline')}</Overline>
            <LanguageSwitcher />
          </div>
          <h1 className="mt-2 font-display text-heading-lg text-content-primary">
            {eventQuery.data?.name ?? ''}
          </h1>
        </div>
        <div className="flex items-center gap-3 text-sm text-content-muted">
          <span>{session.user.email}</span>
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            {t('checkin.logout')}
          </Button>
        </div>
      </div>

      {summary.data ? (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {/* Toute la salle : les contrôlés ET ceux qui ont payé à la porte.
              Le compteur oubliait ces derniers — souvent la moitié du monde. */}
          <Counter
            label={t('checkin.checkedIn')}
            value={summary.data.entered}
            hint={
              summary.data.door_people > 0
                ? t('checkin.doorShare', { count: summary.data.door_people })
                : undefined
            }
            highlight
          />
          <Counter label={t('checkin.remaining')} value={summary.data.remaining} />
          <Counter label={t('checkin.expected')} value={summary.data.expected} />
          <Counter label={t('checkin.bracelets')} value={summary.data.bracelets_distributed} />
        </div>
      ) : null}

      {/* Le compteur de la porte, sous les chiffres du controle : on scanne
          d'abord ceux qui ont reserve, on encaisse ensuite ceux qui arrivent
          sans rien. */}
      <DoorCounter eventId={eventId} />

      {/* Colonnes bornées (`minmax(0, …)`) : sans elles, la colonne d'un
          téléphone prenait la largeur du plus large bouton du résultat et la
          page défilait de côté. */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        <div>
          {scanning ? (
            <>
              <QrScanner onDetected={submit} paused={paused} />
              <Button
                variant="outline"
                className="mt-3 w-full"
                onClick={() => setScanning(false)}
              >
                {t('checkin.stopScan')}
              </Button>
            </>
          ) : (
            <div className="flex min-h-56 w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-line bg-bg-elevated p-8 text-center">
              <p className="text-sm text-content-muted">{t('checkin.cameraIdle')}</p>
              <Button size="lg" onClick={() => setScanning(true)}>
                {t('checkin.startScan')}
              </Button>
            </div>
          )}

          <form
            className="mt-4 flex gap-2"
            onSubmit={(submitEvent) => {
              submitEvent.preventDefault()
              submit(manualToken)
              setManualToken('')
            }}
          >
            {/* Un QR peut être illisible : écran cassé, luminosité au minimum.
                Le code imprimé sous le billet, ou le numéro de commande, se
                dicte — en majuscules ou non. */}
            <input
              value={manualToken}
              onChange={(changeEvent) => setManualToken(changeEvent.target.value)}
              placeholder={t('checkin.manualPlaceholder')}
              autoCapitalize="characters"
              className="min-w-0 flex-1 rounded-md border border-line bg-bg-base px-3 py-2 font-mono text-sm text-content-primary"
            />
            <Button type="submit" variant="outline" size="sm">
              {t('checkin.check')}
            </Button>
          </form>
        </div>

        <div aria-live="assertive">
          {scan.isPending ? (
            <Card className="flex h-64 items-center justify-center">
              <span className="text-content-muted">{t('checkin.checking')}</span>
            </Card>
          ) : outcome && tone ? (
            <Card className={`border-2 ${tone.border}`}>
              <p className={`font-display text-display-md ${tone.text}`}>
                {t(verdict ?? tone.label)}
              </p>

              {outcome.full_name ? (
                <p className="mt-4 font-display text-heading-lg text-content-primary">
                  {outcome.full_name}
                </p>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {/* La nature du billet en premier : c'est elle qui dit combien
                    de personnes ce QR fait entrer. */}
                {outcome.ticket_type ? <Badge>{outcome.ticket_type}</Badge> : null}
                {outcome.order_number ? (
                  <Badge tone="neutral">{outcome.order_number}</Badge>
                ) : null}
                {outcome.participants.length > 0 ? (
                  <Badge tone="neutral">
                    {t('checkin.groupSize', { count: outcome.participants.length })}
                  </Badge>
                ) : outcome.table_party_size ? (
                  <Badge tone="neutral">
                    {t('checkin.groupSize', { count: outcome.table_party_size })}
                  </Badge>
                ) : null}
                {outcome.table_label ? (
                  <Badge tone="champagne">{outcome.table_label}</Badge>
                ) : null}
              </div>

              {collected ? (
                <div
                  role="status"
                  className="mt-4 rounded-lg border-2 border-state-success bg-state-success/5 p-4"
                >
                  <p className="text-sm text-content-secondary">
                    {t('checkin.collect.done', { register: collected.register_label })}
                  </p>
                  <p className="mt-2 overline text-content-muted">{t('checkin.collect.giveBack')}</p>
                  <p className="font-display text-display-sm tabular-nums text-state-success">
                    {formatMoney(
                      {
                        amount_minor: collected.change_minor,
                        currency: outcome.currency ?? 'EUR',
                      },
                      i18n.language,
                    )}
                  </p>
                </div>
              ) : null}

              {outcome.food.length > 0 ? (
                <p className="mt-4 text-sm text-content-secondary">
                  {t('checkin.food')} : {outcome.food.join(' · ')}
                </p>
              ) : null}

              {/* Une table sans place nominative : ses prénoms pour reconnaître
                  le groupe, et ce que ce QR permet — ou non. */}
              {outcome.participants.length === 0 && outcome.table_label ? (
                <div className="mt-4 space-y-1 text-sm">
                  {outcome.table_guests && outcome.table_guests.length > 0 ? (
                    <p className="break-words text-content-secondary">
                      {t('checkin.tableGuests')} : {outcome.table_guests.join(' · ')}
                    </p>
                  ) : null}
                  {outcome.table_admits === 0 ? (
                    <p className="text-champagne-300">{t('checkin.tableNoAdmission')}</p>
                  ) : outcome.table_admits ? (
                    <p className="text-champagne-300">
                      {t('checkin.tableAdmission', { count: outcome.table_admits })}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {outcome.result === 'already_checked_in' && outcome.checked_in_at ? (
                <p className="mt-4 text-sm text-content-secondary">
                  {t('checkin.alreadyDetail', {
                    time: new Date(outcome.checked_in_at).toLocaleTimeString(),
                    staff: outcome.checked_in_by ?? '—',
                  })}
                </p>
              ) : null}

              {outcome.result === 'unpaid' && outcome.pays_at_door ? (
                <>
                  {/* Une réservation réglée à l'entrée : ce n'est pas un oubli,
                      c'est prévu. Le montant en grand — c'est la somme à
                      demander, et la seule que la porte voit. */}
                  <div
                    role="alert"
                    className="mt-4 rounded-lg border-2 border-ember-400 bg-ember-400/10 p-4"
                  >
                    <p className="overline text-ember-300">{t('checkin.atDoor.title')}</p>
                    <p className="mt-1 font-display text-display-sm tabular-nums text-content-primary">
                      {formatMoney(
                        {
                          amount_minor:
                            (outcome.amount_due_minor ?? 0) + (outcome.supplement_due_minor ?? 0),
                          currency: outcome.currency ?? 'EUR',
                        },
                        i18n.language,
                      )}
                    </p>
                    <p className="mt-1 text-sm text-content-secondary">{t('checkin.atDoor.hint')}</p>
                  </div>
                  {outcome.order_id ? (
                    <DoorCollect
                      outcome={outcome}
                      eventId={eventId}
                      onCollected={(result) => {
                        setCollected(result)
                        setOutcome(result.scan)
                      }}
                    />
                  ) : null}
                </>
              ) : outcome.result === 'unpaid' ? (
                <>
                  <p className="mt-4 text-sm text-ember-300">
                    {t('checkin.unpaidDetail')}
                    {/* Combien il reste à encaisser : sans ce chiffre, le
                        contrôle doit aller le chercher ailleurs pendant que
                        trente personnes patientent derrière. */}
                    {outcome.amount_due_minor && outcome.currency
                      ? ` — ${t('checkin.amountDue', {
                          amount: formatMoney(
                            {
                              amount_minor: outcome.amount_due_minor,
                              currency: outcome.currency,
                            },
                            i18n.language,
                          ),
                        })}`
                      : ''}
                  </p>
                  {outcome.order_id ? (
                    <DoorCollect
                      outcome={outcome}
                      eventId={eventId}
                      onCollected={(result) => {
                        setCollected(result)
                        setOutcome(result.scan)
                      }}
                    />
                  ) : null}
                </>
              ) : null}

              {/* L'entrée est payée, des plats ajoutés ne le sont pas : le
                  groupe entre quand même. La porte peut encaisser les plats au
                  passage — dans son tiroir, sans toucher à la table. */}
              {outcome.result !== 'unpaid' &&
              (outcome.supplement_due_minor ?? 0) > 0 &&
              outcome.order_id ? (
                <>
                  <p className="mt-4 text-sm text-ember-300">
                    {t('checkin.supplementDue', {
                      amount: formatMoney(
                        {
                          amount_minor: outcome.supplement_due_minor ?? 0,
                          currency: outcome.currency ?? 'EUR',
                        },
                        i18n.language,
                      ),
                    })}
                  </p>
                  <DoorCollect
                    outcome={outcome}
                    eventId={eventId}
                    onCollected={(result) => {
                      setCollected(result)
                      setOutcome(result.scan)
                    }}
                  />
                </>
              ) : null}

              {/* Le groupe, nom par nom. Chaque ligne a son bouton : on valide
                  la personne qu'on a devant soi, pas le billet. */}
              {outcome.participants.length > 0 ? (
                <ul className="mt-6 space-y-2">
                  {outcome.participants.map((person) => (
                    <ParticipantRow
                      key={person.attendee_id}
                      person={person}
                      locked={!outcome.paid}
                      pending={checkIn.isPending}
                      onCheckIn={() =>
                        checkIn.mutate(
                          { attendeeId: person.attendee_id },
                          {
                            onSuccess: (updated) =>
                              setOutcome({
                                ...outcome,
                                participants: outcome.participants.map((entry) =>
                                  entry.attendee_id === person.attendee_id
                                    ? {
                                        ...entry,
                                        checked_in_at: updated.checked_in_at,
                                        checked_in_by: updated.checked_in_by,
                                      }
                                    : entry,
                                ),
                              }),
                          },
                        )
                      }
                      onBracelet={() =>
                        bracelet.mutate(
                          { attendeeId: person.attendee_id },
                          {
                            onSuccess: () =>
                              setOutcome({
                                ...outcome,
                                participants: outcome.participants.map((entry) =>
                                  entry.attendee_id === person.attendee_id
                                    ? { ...entry, single_bracelet: 'distributed' }
                                    : entry,
                                ),
                              }),
                          },
                        )
                      }
                      onUndo={() => {
                        const reason = askUndoReason()
                        if (!reason) return
                        undo.mutate(
                          { attendeeId: person.attendee_id, reason },
                          {
                            onSuccess: () =>
                              setOutcome({
                                ...outcome,
                                participants: outcome.participants.map((entry) =>
                                  entry.attendee_id === person.attendee_id
                                    ? { ...entry, checked_in_at: null, checked_in_by: null }
                                    : entry,
                                ),
                              }),
                          },
                        )
                      }}
                    />
                  ))}
                </ul>
              ) : null}

              {/* Une réservation de table : on lui donne son numéro ici,
                  pendant que le groupe est devant nous. */}
              {outcome.reservation_id && outcome.result !== 'unpaid' ? (
                <TablePicker
                  reservationId={outcome.reservation_id}
                  partySize={outcome.table_party_size}
                  assignedNumber={outcome.table_number}
                  assignedName={outcome.table_name}
                  seatedAt={outcome.table_seated_at ?? null}
                  seatedBy={outcome.table_seated_by ?? null}
                  eventId={eventId}
                  onAssigned={(tableNumber, tableName) =>
                    setOutcome({ ...outcome, table_number: tableNumber, table_name: tableName })
                  }
                />
              ) : null}

              {/* RULE 7 — « un bracelet est attendu », jamais pourquoi.
                  Ce bloc ne sert qu'au scan d'un ancien QR par personne : dans
                  un groupe, le bracelet se remet ligne par ligne, au nom près. */}
              {outcome.participants.length > 0 ? null : outcome.single_bracelet ===
                  'requested' && outcome.attendee_id ? (
                <div className="mt-6 rounded-lg border border-champagne-400/40 bg-champagne-400/5 px-5 py-4">
                  <p className="text-sm text-champagne-300">{t('checkin.braceletExpected')}</p>
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="champagne"
                    disabled={bracelet.isPending}
                    onClick={() => {
                      const attendeeId = outcome.attendee_id
                      if (!attendeeId) return
                      bracelet.mutate(
                        { attendeeId },
                        {
                          onSuccess: () =>
                            setOutcome({ ...outcome, single_bracelet: 'distributed' }),
                        },
                      )
                    }}
                  >
                    {t('checkin.braceletGiven')}
                  </Button>
                </div>
              ) : outcome.single_bracelet === 'distributed' ? (
                <p className="mt-6 text-sm text-state-success">{t('checkin.braceletDone')}</p>
              ) : null}

              {notice ? <p className="mt-4 text-sm text-ember-300">{notice}</p> : null}
              {actionError ? (
                <p className="mt-4 text-sm text-state-danger">{actionError}</p>
              ) : null}

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => {
                    setOutcome(null)
                    setCollected(null)
                    setNotice(null)
                  }}
                >
                  {t('checkin.next')}
                </Button>

                {outcome.result === 'valid' && outcome.attendee_id ? (
                  <Button
                    variant="ghost"
                    disabled={undo.isPending}
                    onClick={() => {
                      const attendeeId = outcome.attendee_id
                      if (!attendeeId) return
                      const reason = askUndoReason()
                      if (!reason) return
                      undo.mutate({ attendeeId, reason }, { onSuccess: () => setOutcome(null) })
                    }}
                  >
                    {t('checkin.undo')}
                  </Button>
                ) : null}
              </div>
            </Card>
          ) : (
            <Card className="flex h-64 items-center justify-center text-center">
              <p className="text-content-muted">{t('checkin.waiting')}</p>
            </Card>
          )}

          {scan.isError ? (
            <p className="mt-4 text-sm text-state-danger">{errorText(scan.error)}</p>
          ) : null}
          {!outcome && notice ? <p className="mt-4 text-sm text-ember-300">{notice}</p> : null}
        </div>
      </div>
    </Container>
  )
}

/**
 * Encaisser ici une commande pas encore réglée.
 *
 * Dans la caisse de l'entrée — ou celle de toute la carte —, que la personne
 * au contrôle doit tenir : c'est elle qui comptera ce tiroir à la fin de la
 * nuit. En espèces, on saisit ce qu'on a **reçu** ; la monnaie vient du
 * serveur. PayPal : on vérifie le paiement sur le téléphone de l'invité.
 */
function DoorCollect({
  outcome,
  eventId,
  onCollected,
}: {
  outcome: ScanResponse
  eventId: string | undefined
  onCollected: (result: DoorCollectResult) => void
}) {
  const { t, i18n } = useTranslation()
  const errorText = useApiErrorText()
  const till = useDoorTill(eventId, true)
  const collect = useCollectAtDoor(eventId)
  const [method, setMethod] = useState<DoorCollectMethod>('cash')
  const [received, setReceived] = useState('')

  const currency = outcome.currency ?? 'EUR'
  // L'entrée si elle n'est pas réglée, et les plats ajoutés encore dus : le
  // serveur encaisse le tout en un geste, chaque part sur son propre règlement.
  const due = (outcome.amount_due_minor ?? 0) + (outcome.supplement_due_minor ?? 0)
  const digits = minorUnits(currency)
  const value = Number.parseFloat(received.replace(',', '.'))
  const receivedMinor =
    Number.isFinite(value) && value >= 0 ? Math.round(value * 10 ** digits) : null
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency }, i18n.language)

  if (till.isPending) return <Skeleton className="mt-4 h-24 w-full" />

  let blocked: ReactNode = null
  if (isForbidden(till.error)) {
    blocked = t('checkin.collect.noRight')
  } else if (!till.data) {
    blocked = (
      <>
        {t('checkin.collect.noTill')}{' '}
        <Link to="/ops/pos" className="text-champagne-300 underline">
          {t('checkin.collect.openTill')}
        </Link>
      </>
    )
  } else if (till.data.station && till.data.station !== 'door' && till.data.station !== 'none') {
    blocked = t('checkin.collect.wrongTill', { register: till.data.register_label })
  }

  return (
    <div className="mt-4 space-y-3 rounded-md border border-ember-400/40 bg-bg-base p-4">
      <p className="overline text-content-muted">{t('checkin.collect.title')}</p>
      {/* Réglée à l'entrée : le montant est déjà en grand, juste au-dessus. */}
      {outcome.pays_at_door && outcome.result === 'unpaid' ? null : (
        <p className="font-display text-heading-md tabular-nums text-content-primary">
          {money(due)}
        </p>
      )}
      {blocked ? (
        <p className="text-sm text-content-secondary">{blocked}</p>
      ) : (
        <>
          {/* Espèces (monnaie rendue), carte au terminal de l'entrée, PayPal
              vu sur le téléphone de l'invité : les trois que l'e-mail annonce. */}
          <div className="grid grid-cols-3 gap-2">
            {(['cash', 'card', 'paypal'] as const).map((option) => (
              <Button
                key={option}
                size="sm"
                variant={method === option ? 'ember' : 'outline'}
                // Trois boutons côte à côte sur un téléphone : ils se
                // partagent la largeur au lieu d'imposer la leur.
                className="min-w-0 px-2"
                onClick={() => setMethod(option)}
              >
                {t(`checkin.collect.method.${option}`)}
              </Button>
            ))}
          </div>
          {method === 'cash' ? (
            <label className="block text-caption text-content-muted">
              {t('checkin.collect.received')}
              <div className="mt-1 flex gap-2">
                <input
                  inputMode="decimal"
                  value={received}
                  onChange={(changeEvent) => setReceived(changeEvent.target.value)}
                  className="h-12 min-w-0 flex-1 rounded-md border border-line bg-bg-elevated px-3 text-lg text-content-primary"
                />
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => setReceived((due / 10 ** digits).toFixed(digits))}
                >
                  {t('checkin.collect.exact')}
                </Button>
              </div>
            </label>
          ) : (
            <p className="text-caption text-content-muted">
              {method === 'card' ? t('checkin.collect.cardHint') : t('checkin.collect.paypalHint')}
            </p>
          )}
          <Button
            size="lg"
            className="w-full"
            disabled={
              collect.isPending ||
              (method === 'cash' && (receivedMinor === null || receivedMinor < due))
            }
            onClick={() => {
              if (!outcome.order_id) return
              collect.mutate(
                {
                  orderId: outcome.order_id,
                  method,
                  received_minor: method === 'cash' ? receivedMinor : null,
                },
                { onSuccess: onCollected },
              )
            }}
          >
            {method === 'cash'
              ? t('checkin.collect.confirmCash')
              : method === 'card'
                ? t('checkin.collect.confirmCard')
                : t('checkin.collect.confirmPaypal')}
          </Button>
          {collect.isError ? (
            <p className="text-sm text-state-danger">{errorText(collect.error)}</p>
          ) : null}
        </>
      )}
    </div>
  )
}

/**
 * Une personne du groupe, telle qu'on la valide au contrôle.
 *
 * Le bouton est large et seul sur sa ligne : il est pressé au pouce, dans le
 * noir, par quelqu'un qui tient la tablette d'une main. Une fois la personne
 * entrée, il cède la place à l'heure d'entrée — et à l'annulation, parce qu'une
 * erreur de ligne arrive quand deux frères ont le même prénom.
 */
function ParticipantRow({
  person,
  locked,
  pending,
  onCheckIn,
  onBracelet,
  onUndo,
}: {
  person: ScanParticipant
  /** Vrai tant que la commande n'est pas réglée : on n'ouvre pas la porte. */
  locked: boolean
  pending: boolean
  onCheckIn: () => void
  onBracelet: () => void
  onUndo: () => void
}) {
  const { t } = useTranslation()
  const entered = person.checked_in_at !== null

  return (
    <li
      className={`rounded-lg border px-4 py-3 ${
        entered ? 'border-state-success/40 bg-state-success/5' : 'border-line-subtle'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-heading-sm text-content-primary">
            {person.full_name || t('checkin.unnamedGuest')}
          </p>
          <p className="text-caption text-content-muted">
            {[person.ticket_type, person.ticket_code].filter(Boolean).join(' · ')}
          </p>
        </div>

        {entered ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-state-success">
              ✓ {new Date(person.checked_in_at as string).toLocaleTimeString()}
            </span>
            <Button variant="ghost" size="sm" onClick={onUndo}>
              {t('checkin.undo')}
            </Button>
          </div>
        ) : (
          <Button size="sm" disabled={locked || pending} onClick={onCheckIn}>
            {locked ? t('checkin.lockedByPayment') : t('checkin.letIn')}
          </Button>
        )}
      </div>

      {/* RULE 7 — « un bracelet est attendu », jamais pourquoi. */}
      {person.single_bracelet === 'requested' ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-champagne-400/20 pt-3">
          <span className="text-caption text-champagne-300">
            {t('checkin.braceletExpected')}
          </span>
          <Button size="sm" variant="champagne" onClick={onBracelet}>
            {t('checkin.braceletGiven')}
          </Button>
        </div>
      ) : person.single_bracelet === 'distributed' ? (
        <p className="mt-3 text-caption text-state-success">{t('checkin.braceletDone')}</p>
      ) : null}
    </li>
  )
}

function Counter({
  label,
  value,
  hint,
  highlight = false,
}: {
  label: string
  value: number
  hint?: string | undefined
  highlight?: boolean
}) {
  return (
    <div className="rounded-lg border border-line-subtle bg-bg-elevated px-4 py-3">
      <p className="overline text-content-muted">{label}</p>
      <p
        className={`mt-1 font-display text-heading-lg tabular-nums ${
          highlight ? 'text-champagne-300' : 'text-content-primary'
        }`}
      >
        {value}
      </p>
      {hint ? <p className="text-caption text-content-muted">{hint}</p> : null}
    </div>
  )
}
